import React, { useState } from 'react';
import { ApiError, apiFetch } from '../../lib/api';
import { boardPageCount, boardPageText, clampBoardPage } from '../../lib/shooting';
import type { TournamentDetail } from '../../hooks/useScoringTournament';

interface DisplayPageCardProps {
  tournamentId: string;
  slug: string;
  leaderboard: TournamentDetail['leaderboard'];
  onSaved: (leaderboard: TournamentDetail['leaderboard']) => void;
}

/** Prev / Next for the page of ranks that big screens show. The public board follows it live. */
export function DisplayPageCard({ tournamentId, slug, leaderboard, onSaved }: DisplayPageCardProps) {
  const rowCount = leaderboard.rows.length;
  const page = clampBoardPage(leaderboard.displayPage ?? 0, rowCount);
  const pages = boardPageCount(rowCount);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go(step: 1 | -1) {
    setBusy(true);
    setError(null);
    try {
      const body = await apiFetch<{ leaderboard: TournamentDetail['leaderboard'] }>(
        `/api/admin/shooting/tournaments/${tournamentId}/display-page`,
        { method: 'PATCH', body: JSON.stringify({ step }) },
      );
      onSaved(body.leaderboard);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the page.');
    } finally {
      setBusy(false);
    }
  }

  const button =
    'min-h-11 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] px-4 text-sm font-medium text-[var(--color-text-primary)] disabled:opacity-40';
  return (
    <section className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-4" aria-label="Big-screen page">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-medium text-[var(--color-text-secondary)]">Big-screen board</h2>
          <p className="text-base font-semibold tabular-nums text-[var(--color-text-primary)]" role="status">
            {boardPageText(page, rowCount)}
          </p>
          <p className="text-xs text-[var(--color-text-tertiary)]">
            Page {page + 1} of {pages}. TVs and laptops on{' '}
            <a href={`/scoreboard/${slug}`} target="_blank" rel="noreferrer" className="underline underline-offset-2">
              the live board
            </a>{' '}
            switch with you; phones show everyone.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button type="button" className={button} disabled={busy || page === 0} onClick={() => void go(-1)}>
            ‹ Prev
          </button>
          <button type="button" className={button} disabled={busy || page >= pages - 1} onClick={() => void go(1)}>
            Next ›
          </button>
        </div>
      </div>
      {error ? (
        <p className="mt-2 text-sm font-medium text-[var(--color-error-text)]" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
